"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BinaryWriter = exports.FlagBits = exports.ConstType = void 0;
exports.encodeBytecodeToBuffer = encodeBytecodeToBuffer;
exports.bufferToBase64 = bufferToBase64;
exports.ConstType = {
    Null: 0,
    Undefined: 1,
    BooleanFalse: 2,
    BooleanTrue: 3,
    Int8: 4,
    Int16: 5,
    Int32: 6,
    Float64: 7,
    String: 8,
    BigInt: 9,
    Regex: 10
};
exports.FlagBits = {
    IsArrow: 1 << 0,
    IsAsync: 1 << 1,
    IsGenerator: 1 << 2,
    HasFunctionName: 1 << 3,
    HasOpcodeSeed: 1 << 4,
    HasOpcodeMap: 1 << 5,
    HasJumpKey: 1 << 6,
    HasBytecodeKey: 1 << 7,
    HasSmSeed: 1 << 8,
    HasSmState: 1 << 9,
    HasSeKey: 1 << 10,
    HasJumps: 1 << 11,
    HasExceptions: 1 << 12
};
class BinaryWriter {
    constructor(initialSize = 1024) {
        this.offset = 0;
        this.buffer = new Uint8Array(initialSize);
        this.view = new DataView(this.buffer.buffer);
    }
    writeUint8(value) {
        this.ensureCapacity(1);
        this.buffer[this.offset++] = value & 0xFF;
    }
    writeUint16(value) {
        this.ensureCapacity(2);
        this.view.setUint16(this.offset, value, true);
        this.offset += 2;
    }
    writeUint32(value) {
        this.ensureCapacity(4);
        this.view.setUint32(this.offset, value, true);
        this.offset += 4;
    }
    writeInt32(value) {
        this.ensureCapacity(4);
        this.view.setInt32(this.offset, value, true);
        this.offset += 4;
    }
    writeFloat64(value) {
        this.ensureCapacity(8);
        this.view.setFloat64(this.offset, value, true);
        this.offset += 8;
    }
    writeVarint(value) {
        const unsigned = value >= 0 ? value * 2 : (-value * 2) - 1;
        let v = unsigned;
        while (v >= 0x80) {
            this.writeUint8((v & 0x7F) | 0x80);
            v >>>= 7;
        }
        this.writeUint8(v);
    }
    writeString(str) {
        const encoded = new TextEncoder().encode(str);
        this.writeVarint(encoded.length);
        this.ensureCapacity(encoded.length);
        this.buffer.set(encoded, this.offset);
        this.offset += encoded.length;
    }
    getBuffer() {
        return this.buffer.slice(0, this.offset);
    }
    getOffset() {
        return this.offset;
    }
    ensureCapacity(needed) {
        if (this.offset + needed <= this.buffer.length) {
            return;
        }
        const newSize = Math.max(this.buffer.length * 2, this.offset + needed);
        const newBuffer = new Uint8Array(newSize);
        newBuffer.set(this.buffer);
        this.buffer = newBuffer;
        this.view = new DataView(this.buffer.buffer);
    }
}
exports.BinaryWriter = BinaryWriter;
function writeConstant(writer, value) {
    if (value === null) {
        writer.writeUint8(exports.ConstType.Null);
    }
    else if (value === undefined) {
        writer.writeUint8(exports.ConstType.Undefined);
    }
    else if (value === false) {
        writer.writeUint8(exports.ConstType.BooleanFalse);
    }
    else if (value === true) {
        writer.writeUint8(exports.ConstType.BooleanTrue);
    }
    else if (typeof value === 'number') {
        if (Number.isInteger(value) && value >= -128 && value <= 127) {
            writer.writeUint8(exports.ConstType.Int8);
            writer.writeUint8(value & 0xFF);
        }
        else if (Number.isInteger(value) && value >= -32768 && value <= 32767) {
            writer.writeUint8(exports.ConstType.Int16);
            writer.writeUint16(value & 65535);
        }
        else if (Number.isInteger(value) && value >= -2147483648 && value <= 2147483647) {
            writer.writeUint8(exports.ConstType.Int32);
            writer.writeInt32(value);
        }
        else {
            writer.writeUint8(exports.ConstType.Float64);
            writer.writeFloat64(value);
        }
    }
    else if (typeof value === 'string') {
        writer.writeUint8(exports.ConstType.String);
        writer.writeString(value);
    }
    else if (typeof value === 'bigint') {
        writer.writeUint8(exports.ConstType.BigInt);
        writer.writeString(value.toString());
    }
    else if (value instanceof RegExp) {
        writer.writeUint8(exports.ConstType.Regex);
        writer.writeString(value.source);
        writer.writeString(value.flags);
    }
}
function encodeBytecodeToBuffer(bytecode, keyMapping) {
    const writer = new BinaryWriter();
    writer.writeUint8(1);
    let flags = 0;
    if (bytecode.isArrow) {
        flags |= exports.FlagBits.IsArrow;
    }
    if (bytecode.isAsync) {
        flags |= exports.FlagBits.IsAsync;
    }
    if (bytecode.isGenerator) {
        flags |= exports.FlagBits.IsGenerator;
    }
    if (bytecode.functionNameIndex !== undefined) {
        flags |= exports.FlagBits.HasFunctionName;
    }
    if (bytecode.opcodeSeed !== undefined) {
        flags |= exports.FlagBits.HasOpcodeSeed;
    }
    if (bytecode.opcodeMap) {
        flags |= exports.FlagBits.HasOpcodeMap;
    }
    if (bytecode.jumpKey !== undefined) {
        flags |= exports.FlagBits.HasJumpKey;
    }
    if (bytecode.bytecodeKey !== undefined) {
        flags |= exports.FlagBits.HasBytecodeKey;
    }
    if (bytecode.smSeed !== undefined) {
        flags |= exports.FlagBits.HasSmSeed;
    }
    if (bytecode.smState !== undefined) {
        flags |= exports.FlagBits.HasSmState;
    }
    if (bytecode.seKey !== undefined) {
        flags |= exports.FlagBits.HasSeKey;
    }
    const hasJumps = bytecode.instructions.some((i) => i.target !== undefined);
    const hasExceptions = bytecode.instructions.some((i) => i.catchIndex !== undefined || i.finallyIndex !== undefined || i.endIndex !== undefined);
    if (hasJumps) {
        flags |= exports.FlagBits.HasJumps;
    }
    if (hasExceptions) {
        flags |= exports.FlagBits.HasExceptions;
    }
    writer.writeUint16(flags);
    writer.writeVarint(bytecode.params);
    writer.writeVarint(bytecode.locals);
    if (flags & exports.FlagBits.HasFunctionName) {
        writer.writeVarint(bytecode.functionNameIndex);
    }
    if (flags & exports.FlagBits.HasOpcodeSeed) {
        writer.writeUint32(bytecode.opcodeSeed);
    }
    if (flags & exports.FlagBits.HasOpcodeMap) {
        const entries = Object.entries(bytecode.opcodeMap);
        writer.writeVarint(entries.length);
        for (const [logical, randomized] of entries) {
            writer.writeVarint(parseInt(logical, 10));
            writer.writeVarint(randomized);
        }
    }
    if (flags & exports.FlagBits.HasJumpKey) {
        writer.writeUint32(bytecode.jumpKey);
    }
    if (flags & exports.FlagBits.HasBytecodeKey) {
        writer.writeUint32(bytecode.bytecodeKey);
    }
    if (flags & exports.FlagBits.HasSmSeed) {
        writer.writeUint32(bytecode.smSeed);
    }
    if (flags & exports.FlagBits.HasSmState) {
        writer.writeVarint(bytecode.smState);
    }
    if (flags & exports.FlagBits.HasSeKey) {
        writer.writeUint32(bytecode.seKey);
    }
    writer.writeVarint(bytecode.constants.length);
    for (const c of bytecode.constants) {
        writeConstant(writer, c);
    }
    writer.writeVarint(bytecode.instructions.length);
    for (const instr of bytecode.instructions) {
        writer.writeVarint(instr.op);
        if (instr.arg === undefined || instr.arg === null) {
            writer.writeUint8(exports.ConstType.Null);
        }
        else if (typeof instr.arg === 'number') {
            if (instr.arg2 !== undefined) {
                const packed = (instr.arg2 << 16) | instr.arg;
                writer.writeUint8(exports.ConstType.Int32);
                writer.writeInt32(packed);
            }
            else if (Number.isInteger(instr.arg) && instr.arg >= -128 && instr.arg <= 127) {
                writer.writeUint8(exports.ConstType.Int8);
                writer.writeUint8(instr.arg & 0xFF);
            }
            else if (Number.isInteger(instr.arg) && instr.arg >= -32768 && instr.arg <= 32767) {
                writer.writeUint8(exports.ConstType.Int16);
                writer.writeUint16(instr.arg & 65535);
            }
            else if (Number.isInteger(instr.arg)) {
                writer.writeUint8(exports.ConstType.Int32);
                writer.writeInt32(instr.arg);
            }
            else {
                writer.writeUint8(exports.ConstType.Float64);
                writer.writeFloat64(instr.arg);
            }
        }
        else if (typeof instr.arg === 'string') {
            writer.writeUint8(exports.ConstType.String);
            writer.writeString(instr.arg);
        }
    }
    if (flags & exports.FlagBits.HasJumps) {
        const jumps = [];
        bytecode.instructions.forEach((instr, idx) => {
            if (instr.target !== undefined) {
                const encodedTarget = bytecode.jumpKey !== undefined ? instr.target ^ bytecode.jumpKey : instr.target;
                jumps.push([idx, encodedTarget]);
            }
        });
        writer.writeVarint(jumps.length);
        for (const [idx, target] of jumps) {
            writer.writeVarint(idx);
            writer.writeVarint(target);
        }
    }
    if (flags & exports.FlagBits.HasExceptions) {
        const exceptions = [];
        const collectExceptions = (instr, idx) => {
            var _a, _b, _c;
            if (instr.catchIndex !== undefined || instr.finallyIndex !== undefined || instr.endIndex !== undefined) {
                const catchVal = (_a = instr.catchIndex) !== null && _a !== void 0 ? _a : -1;
                const finallyVal = (_b = instr.finallyIndex) !== null && _b !== void 0 ? _b : -1;
                const endVal = (_c = instr.endIndex) !== null && _c !== void 0 ? _c : -1;
                const jk = bytecode.jumpKey;
                exceptions.push([
                    idx,
                    jk !== undefined && catchVal >= 0 ? catchVal ^ jk : catchVal,
                    jk !== undefined && finallyVal >= 0 ? finallyVal ^ jk : finallyVal,
                    jk !== undefined && endVal >= 0 ? endVal ^ jk : endVal
                ]);
            }
        };
        bytecode.instructions.forEach(collectExceptions);
        writer.writeVarint(exceptions.length);
        for (const [idx, catchIdx, finallyIdx, endIdx] of exceptions) {
            writer.writeVarint(idx);
            writer.writeVarint(catchIdx + 1);
            writer.writeVarint(finallyIdx + 1);
            writer.writeVarint(endIdx + 1);
        }
    }
    return writer.getBuffer();
}
function bufferToBase64(buffer) {
    if (typeof Buffer !== 'undefined') {
        return Buffer.from(buffer).toString('base64');
    }
    let binary = '';
    for (const byte of buffer) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
}
